import React, { useRef, useState } from 'react';
import { UploadCloud, FileSpreadsheet, Trash2 } from 'lucide-react';
import clsx from 'clsx';

const DEFAULT_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Drag & drop area for a single file.
 *
 * Built on React's synthetic drag events plus a hidden file input reached
 * through a ref, so it needs neither an external dropzone dependency nor any
 * direct DOM lookup.
 *
 * @param {object} props
 * @param {File|null} props.file - Currently staged file, if any
 * @param {(file: File) => void} props.onFileSelected
 * @param {() => void} props.onClear
 * @param {Array<string>} [props.extensions=['.xlsx', '.xls']] - Accepted extensions
 * @param {string} [props.hint] - Copy shown under the call to action
 * @param {string} [props.error] - Message rendered under the zone
 * @param {boolean} [props.disabled=false]
 */
export const FileDropzone = ({
  file,
  onFileSelected,
  onClear,
  extensions = DEFAULT_EXTENSIONS,
  hint,
  error,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejected, setRejected] = useState(false);

  /** @param {File} candidate */
  const accepts = (candidate) => {
    const suffixes = extensions.map((extension) => extension.toLowerCase());
    return suffixes.some((suffix) => candidate.name.toLowerCase().endsWith(suffix));
  };

  /** @param {File} candidate */
  const handleCandidate = (candidate) => {
    if (!candidate) return;

    if (!accepts(candidate)) {
      setRejected(true);
      return;
    }

    setRejected(false);
    onFileSelected(candidate);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleCandidate(event.dataTransfer.files?.[0]);
  };

  const handleInputChange = (event) => {
    handleCandidate(event.target.files?.[0]);
    // Allows re-selecting the same file after clearing it.
    event.target.value = '';
  };

  if (file) {
    return (
      <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-ninja-green/30 bg-ninja-greenLight dark:bg-ninja-green/10">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-white dark:bg-[#1a1a1a] flex items-center justify-center text-ninja-green shrink-0">
            <FileSpreadsheet size={20} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-ninja-dark dark:text-white truncate">
              {file.name}
            </p>
            <p className="text-xs text-text-secondary">
              {(file.size / 1024).toFixed(0)} KB
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="p-2 text-text-muted hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
          title="Quitar archivo"
        >
          <Trash2 size={18} />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={clsx(
          'w-full flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-2xl border border-dashed transition-all',
          disabled && 'opacity-50 cursor-not-allowed',
          isDragging
            ? 'border-ninja-green bg-ninja-greenLight dark:bg-ninja-green/10'
            : rejected || error
              ? 'border-danger bg-red-50 dark:bg-red-900/10'
              : 'border-border bg-ninja-gray/40 dark:bg-[#111] hover:border-ninja-green'
        )}
      >
        <UploadCloud size={30} className={isDragging ? 'text-ninja-green' : 'text-text-muted'} />
        <span className="text-sm font-semibold text-ninja-dark dark:text-white">
          Arrastra tu archivo o haz clic para seleccionarlo
        </span>
        <span className="text-xs text-text-secondary text-center">
          {hint || `Formatos aceptados: ${extensions.join(', ')}`}
        </span>
      </button>

      {(rejected || error) && (
        <p className="text-danger text-xs mt-2">
          {error || `El archivo debe tener alguna de estas extensiones: ${extensions.join(', ')}.`}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        data-testid="file-dropzone-input"
        accept={extensions.join(',')}
        onChange={handleInputChange}
        className="hidden"
      />
    </>
  );
};
